import React, { useEffect, ReactNode } from 'react';
import useToggle from 'hooks/use-toggle';
import classNames from 'classnames';
import Button from 'atomic/button';
import Icon from 'atomic/icon';
import Iframe from 'modules/shared/components/iframe';
import scrollToElement from 'helpers/scroll-to-element';

import './expander.scss';

export interface ExpanderIframeProps {
    content: string;
    minHeight: number;
    expanded?: boolean;
    shadowColorGradient?: 'white' | 'gray';
    anchorIdName?: string;
    onClose?: () => void;
    isReadMore?: boolean;
}

const ExpanderIframe: React.FC<ExpanderIframeProps> = (
    {
        content,
        minHeight = 300,
        expanded = false,
        shadowColorGradient = 'white',
        anchorIdName,
        onClose,
        isReadMore = false
    }
) => {
    const [show, setShow] = useToggle(expanded);

    useEffect(() => {
        setShow.setToggleState(expanded);
    }, [expanded, setShow]);

    const onCLick = (): void => {
        if (anchorIdName && show) {
            scrollToElement(anchorIdName);
        }

        if (onClose && show) {
            onClose();
        }

        setShow.toggle();
    };

    const renderButton = (): ReactNode => {
        if (isReadMore) {
            return (
                <div
                    className={classNames({
                        'expander__button--read-more': true,
                        'expander__button--show-more': isReadMore && !show,
                        [`expander__button--${shadowColorGradient}`]: isReadMore && !show
                    })}
                >
                    <Button
                        tag="span"
                        size="xs"
                        color="grey"
                        uppercase={true}
                        onClick={onCLick}
                    >
                        {show ? 'Zwiń' : 'Czytaj więcej...'}
                    </Button>
                </div>
            );
        }

        return (
            <div className="expander__button">
                <Button
                    size="lg"
                    color="transparent-secondary"
                    onClick={onCLick}
                    data-cy="show-text"
                >
                    <Icon name={show ? 'minus' : 'plus'} color="secondary" size={18} />
                    {show ? 'Zwiń opis' : 'Rozwiń opis'}
                </Button>
            </div>
        );
    };

    return (
        <div className="expander">
            <div
                className={classNames({
                    'expander__text': true,
                    'expander__text--read-more': isReadMore,
                    'expander__text--show-more': show
                })}
                style={{ height: !show ? minHeight : 'auto' }}
            >
                <Iframe src={content} height={minHeight} />
            </div>

            {!show && (
                <div
                    className={classNames({
                        expander__shadow: true,
                        [`expander__shadow--${shadowColorGradient}`]: true
                    })}
                    style={{ height: !show ? minHeight : 'auto' }}
                />
            )}

            {renderButton()}
        </div>
    );
};

export default ExpanderIframe;
